import { useState } from "react";
import { loginUser } from "../../api/auth";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import Logo from "../../assets/png.png";

export default function LoginPage() {
  const { setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg("");
    try {
      const res = await loginUser(form);
      const { accessToken, user } = res.data;
      localStorage.setItem("accessToken", accessToken);
      setUser(user);
      navigate("/dashboard");
    } catch (err) {
      setMsg(
        err.response?.data?.message ||
          "Login failed. Please check your credentials.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      {/* Decorative circles */}
      <Box
        sx={{
          position: "fixed",
          top: -120,
          right: -120,
          width: 400,
          height: 400,
          borderRadius: "50%",
        }}
      />
      <Box
        sx={{
          position: "fixed",
          bottom: -80,
          left: -80,
          width: 300,
          height: 300,
          borderRadius: "50%",
        }}
      />

      {/* Logo / brand mark */}
      <Box
        component="img"
        src={Logo}
        alt="Client Portal"
        sx={{
          width: "100%",
          maxWidth: 220,
          height: "auto",
          objectFit: "contain",
          display: "block",
          mb: 3,
          position: "relative",
          zIndex: 1,
        }}
      />

      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 4 },
          width: "100%",
          maxWidth: 420,
          border: "none",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box textAlign="center" mb={3.5}>
          <Typography variant="h5" sx={{ color: "#0f172a", fontWeight: 800 }}>
            Welcome back
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
            Sign in to your Client Portal account
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {msg && (
              <Alert
                severity="error"
                sx={{ borderRadius: "8px", fontSize: "0.85rem" }}
              >
                {msg}
              </Alert>
            )}

            <TextField
              label="Email address"
              name="email"
              type="email"
              onChange={handleChange}
              fullWidth
              required
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon
                      sx={{ fontSize: "1.1rem", color: "#94a3b8" }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              onChange={handleChange}
              fullWidth
              required
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon
                      sx={{ fontSize: "1.1rem", color: "#94a3b8" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOffOutlinedIcon
                          sx={{ fontSize: "1.1rem", color: "#94a3b8" }}
                        />
                      ) : (
                        <VisibilityOutlinedIcon
                          sx={{ fontSize: "1.1rem", color: "#94a3b8" }}
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box textAlign="right" sx={{ mt: -0.5 }}>
              <Typography variant="body2">
                <Link
                  to="/auth/forgot-password"
                  style={{ color: "#e2553c", fontWeight: 500 }}
                >
                  Forgot password?
                </Link>
              </Typography>
            </Box>

            <Button
              variant="contained"
              type="submit"
              disabled={loading}
              size="large"
              fullWidth
              sx={{ mt: 0.5, py: 1.25 }}
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>

            <Typography
              variant="body2"
              textAlign="center"
              sx={{ color: "#64748b" }}
            >
              Don't have an account?{" "}
              <Link
                to="/auth/register"
                style={{ color: "#e2553c", fontWeight: 600 }}
              >
                Create one
              </Link>
            </Typography>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}
